/**
 * Timeline Helper Functions
 * Extracted utilities for timeline instance management
 */

import { UserData, TimelineItem } from '../utils/types';
import { calculateDateRange as calculateItemsDateRange } from '../utils/timelineDataTransformers';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Derives visible slice of data based on pagination and grouping
 */
export function deriveVisibleSlice(
    fullData: UserData[],
    groupBy: string,
    currentPage: number,
    pageSize: number
): UserData[] {
    if (!fullData || fullData.length === 0) return [];

    // If not grouping by teams, simple user slice
    if (groupBy !== 'TEAMS') {
        const start = (currentPage - 1) * pageSize;
        return fullData.slice(start, start + pageSize);
    }

    // Build team map keyed by teamId to stay consistent with createGroupsArray
    interface TeamBucket { teamId: number; teamName: string; users: UserData[] }
    const teamMap = new Map<number, TeamBucket>();
    fullData.forEach((userData) => {
        // Data shape: userData.user.teamId / userData.user.teamName
        const teamId = userData.user?.teamId ?? -1; // -1 for unknown teams
        const teamName = userData.user?.teamName || 'Blanks';
        if (!teamMap.has(teamId)) {
            teamMap.set(teamId, {
                teamId, teamName, users: []
            });
        }
        teamMap.get(teamId)!.users.push(userData);
    });

    // Sort teams lexically by teamName to match existing sorting expectations
    const teamsArray = Array.from(teamMap.values()).sort((a, b) => a.teamName.localeCompare(b.teamName));
    const start = (currentPage - 1) * pageSize;
    const visibleTeams = teamsArray.slice(start, start + pageSize);

    // Flatten users of visible teams preserving original order inside each bucket
    return visibleTeams.flatMap((t) => t.users);
}

/**
 * Gets zoom level configurations based on time format preference
 */
export function getZoomLevels(is12Hour: boolean) {
    const hourFormat = is12Hour ? 'hh:mm A' : 'HH:mm';
    const hourSecondsFormat = is12Hour ? 'hh:mm:ss A' : 'HH:mm:ss';
    const hourDateFormat = is12Hour ? 'ddd DD MMM hh:mm A' : 'ddd DD MMM HH:mm';

    return [
        {
            threshold: 1000 * 60 * 60 * 24 * 30,
            timeAxis: { scale: 'month', step: 1 },
            format: { minorLabels: { month: 'MMM' }, majorLabels: { month: 'YYYY' } }
        },
        {
            threshold: 1000 * 60 * 60 * 24 * 7,
            timeAxis: { scale: 'week', step: 1 },
            format: { minorLabels: { week: 'w' }, majorLabels: { week: 'MMM YYYY' } }
        },
        {
            threshold: 1000 * 60 * 60 * 24 * 2,
            timeAxis: { scale: 'day', step: 1 },
            format: { minorLabels: { day: 'DD' }, majorLabels: { day: 'MMMM YYYY' } }
        },
        {
            threshold: 1000 * 60 * 60 * 12,
            timeAxis: { scale: 'hour', step: 4 },
            format: { minorLabels: { hour: hourFormat }, majorLabels: { hour: 'ddd DD MMM' } }
        },
        {
            threshold: 1000 * 60 * 60 * 4,
            timeAxis: { scale: 'hour', step: 2 },
            format: { minorLabels: { hour: hourFormat }, majorLabels: { hour: 'ddd DD MMM' } }
        },
        {
            threshold: 1000 * 60 * 60 * 2,
            timeAxis: { scale: 'hour', step: 1 },
            format: { minorLabels: { hour: hourFormat }, majorLabels: { hour: 'ddd DD MMM' } }
        },
        {
            threshold: 1000 * 60 * 30,
            timeAxis: { scale: 'minute', step: 15 },
            format: { minorLabels: { minute: hourFormat }, majorLabels: { minute: hourDateFormat } }
        },
        {
            threshold: 1000 * 60 * 15,
            timeAxis: { scale: 'minute', step: 5 },
            format: { minorLabels: { minute: hourFormat }, majorLabels: { minute: hourDateFormat } }
        },
        {
            threshold: 0,
            timeAxis: { scale: 'minute', step: 1 },
            format: { minorLabels: { minute: hourSecondsFormat }, majorLabels: { minute: hourDateFormat } }
        }
    ];
}

/**
 * Calculate date range for timeline display
 */
export function calculateTimelineDateRange(
    itemsArray: TimelineItem[],
    options?: {
        fallbackStart?: Date;
        fallbackEnd?: Date;
        filterStartDate?: string;
        filterEndDate?: string;
    }
): { minDate: Date; maxDate: Date } {
    if (itemsArray.length > 0) {
        // Have data: use dates from items
        const dateRange = calculateItemsDateRange(itemsArray, options?.fallbackStart, options?.fallbackEnd);
        return {
            minDate: dateRange.minDate || new Date(),
            maxDate: dateRange.maxDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        };
    }

    // No data: use filter dates if available, otherwise default
    if (options?.filterStartDate && options?.filterEndDate) {
        return {
            minDate: new Date(options.filterStartDate),
            maxDate: new Date(options.filterEndDate)
        };
    }

    // Fallback to default 7-day range
    return {
        minDate: new Date(),
        maxDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    };
}

/**
 * Gets the header text based on count and grouping mode
 */
export function getHeaderText(count: number | undefined, groupBy?: string): string {
    if (typeof count !== 'number') return 'Users';
    return groupBy === 'TEAMS' ? `Teams and Users (${count})` : `Users (${count})`;
}

/**
 * Computes the current time ("now") for the selected timezone so that the
 * vis-timeline current-time indicator (the red line) lands at the correct
 * visual position.
 *
 * Timeline items are positioned by stripping the UTC offset from a formatted
 * string (via `formatInTimeZone`) and then calling `new Date()` on the result.
 * JS therefore treats the wall-clock time of the selected timezone as if it
 * were a browser-local instant. Applying the identical technique to "now"
 * keeps the red line consistent with the rendered items.
 *
 * @param timezone - The selected timezone (e.g. 'America/New_York'); when
 *   omitted, the real browser-local `new Date()` is returned.
 */
export function getNowForTimezone(timezone?: string): Date {
    if (!timezone) return new Date();
    try {
        const nowInTz = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd'T'HH:mm:ss");
        return new Date(nowInTz);
    } catch {
        // Invalid IANA name (e.g. unvalidated URL param / stale profile data).
        // Fall back to browser-local time instead of breaking timeline init.
        return new Date();
    }
}
